import { useCallback, useEffect, useState } from "react";
import { adminService } from "../../features/admin/service";
import type {
  RecWsHealthResponse,
  RecJobHealthResponse,
  RecRedisOverviewResponse,
  RecFallbackHealthResponse,
  RecFallbackProcessSnapshot,
  RedisNamespacesResponse,
  RedisClientsResponse,
} from "../../features/admin/types";
import Spinner from "../../components/ui/Spinner";
import InfoTooltip from "../../components/common/InfoTooltip";
import RefreshButton from "../../components/admin/RefreshButton";
import { Card, Warnings, num } from "./AdminRealtimeOpsPanels";

/**
 * Panels for Admin ▸ Settings ▸ Rec Redis Health.
 *
 * Sibling of AdminRealtimeOpsPanels, but every read here targets rec_engine's
 * own infrastructure rather than the shared backend/RAG/OCR one. Three
 * failures were previously invisible:
 *
 *  1. A rec-server worker whose rec:events subscriber has died. It keeps its
 *     WebSocket clients connected and logs nothing further — those admin
 *     panels just stop updating while the other three workers look fine.
 *  2. rec's dedicated Redis running out of connections. Its cap is shared by
 *     the rec-server's own pool AND every backend worker's publish_event()
 *     pool, so the demand is not visible from either side alone.
 *  3. A scheduled job that silently stopped firing. locked_job() gates each
 *     tick to one worker, so "nobody ran it" and "one worker ran it" looked
 *     identical until run bookkeeping was added.
 */

const REFRESH_INTERVAL_MS = 5000;

const secs = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : `${num(n)}s`;

const localTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString() : "—";

/** Table header cell with a visible (i) icon explaining what the column means. */
function Th({ children, hint, className }: { children: React.ReactNode; hint: string; className?: string }) {
  return (
    <th className={className ?? ""}>
      <span className="inline-flex items-center">
        {children}
        <InfoTooltip text={hint} />
      </span>
    </th>
  );
}

/** Tab 1 — rec-server sockets and rec:events fan-out, per worker. */
export function RecRealtimePanel() {
  const [data, setData] = useState<RecWsHealthResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await adminService.getRecWsHealth());
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load rec WebSocket health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <Spinner />;
  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!data) return null;

  if (data.reachable === false) {
    return (
      <div>
        <Warnings
          items={[
            `The rec-server could not be reached over HTTP, so its sockets and ` +
              `rec:events subscriber cannot be inspected at all. This state lives ` +
              `only in that process — the backend has no other way to see it. ` +
              `${data.error ?? ""}`,
          ]}
        />
        <p className="text-xs text-slate-500">
          Check RECOM_SERVICE_URL and that the rec-server container is up. Note that
          this failure does NOT mean recommendations are down — the buyer/seller
          scoring endpoints are on the same service, so verify those independently.
        </p>
      </div>
    );
  }

  const stopped = data.subscribers_stopped ?? [];
  // Sent by the backend relay — rec-server cannot see its own --workers value.
  const expected = data.expected_workers ?? data.worker_count;
  const warnings: string[] = [];
  if (stopped.length > 0) {
    warnings.push(
      `${stopped.length} rec-server worker(s) have a STOPPED rec:events ` +
        `subscriber. Their connected admin/seller clients stay open but receive ` +
        `no further events — no error is logged after the initial drop. Restart ` +
        `those processes.`,
    );
  }
  if (data.worker_count < expected) {
    warnings.push(
      `Only ${data.worker_count} of ${expected} configured rec-server workers ` +
        `answered the health probe. A worker that is wedged, or whose probe ` +
        `responder died, is missing from the table below — so its sockets are not ` +
        `counted in the totals either.`,
    );
  }
  if (data.degraded) warnings.push(data.degraded);

  // Flattened across workers rather than nested under each worker's card: a
  // socket lives on exactly one worker, there are usually only a handful in
  // total, and the interesting question ("who is connected right now") reads
  // better as one list than as four short ones.
  const connections = data.workers.flatMap((w) =>
    (w.connections ?? []).map((c) => ({ ...c, worker: w.instance_id })),
  );

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <RefreshButton onRefresh={load} />
      </div>
      <Warnings items={warnings} />

      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Card
          label="Workers answering"
          value={`${num(data.worker_count)} / ${num(expected)}`}
          tone={data.worker_count < expected ? "warn" : "ok"}
          hint="How many rec-server workers replied to the Pub/Sub health probe, out of the --workers count in docker-compose.rec.yml. A worker that is wedged or whose probe responder died is simply absent from the list, so a shortfall here is itself the fault."
        />
        <Card
          label="Rec sockets"
          value={num(data.total_sockets)}
          hint="WebSocket clients currently connected, summed over every worker that answered. Sockets held by a worker that did not answer are not counted, so this can understate the real total."
        />
        <Card
          label="Subscribers stopped"
          value={num(stopped.length)}
          tone={stopped.length ? "bad" : "ok"}
          hint="Workers whose rec:events subscriber task is no longer running. They keep their WebSocket clients connected and log nothing further, so those admin/seller panels go quietly stale rather than erroring."
        />
        <Card
          label="Events channel"
          value={data.events_channel ?? "rec:events"}
          hint="The single Redis Pub/Sub channel every rec_engine event fans out on. All workers subscribe to it, which is why one dead subscriber affects only its own clients."
        />
      </div>

      {(data.channels ?? []).length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 text-sm font-semibold text-slate-200">
            Sockets by channel (all workers combined)
          </h4>
          <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-slate-500">
              <tr>
                <Th className="py-1" hint="WebSocket channel name. `admin` receives every event; each `leads:<seller_id>` is one seller's own inbox.">channel</Th>
                <Th hint="Clients connected to that channel across all answering workers. A client's socket lives on exactly one worker, so these are sums rather than a maximum.">sockets</Th>
              </tr>
            </thead>
            <tbody>
              {(data.channels ?? []).map((c) => (
                <tr key={c.channel} className="border-t border-white/5">
                  <td className="py-1 font-mono text-slate-300">{c.channel}</td>
                  <td className="text-slate-300">{num(c.sockets)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            A client's socket lives on exactly one worker, so these are sums. The{" "}
            <code>admin</code> channel receives every event; <code>leads:&lt;seller_id&gt;</code>{" "}
            channels are per-seller inboxes.
          </p>
        </div>
      )}

      {connections.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 text-sm font-semibold text-slate-200">
            Connected clients ({num(connections.length)})
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-slate-500">
                <tr>
                  <Th className="py-1" hint="The channel this socket subscribed to. `admin` is the rec dashboard and receives every event; `leads:<seller_id>` is one seller's own live lead feed.">channel</Th>
                  <Th hint="Which user this WebSocket connection authenticated as — the admin dashboard or a specific seller's live lead feed. Comes from the JWT's id claim at connect time, not re-verified per message.">caller_id</Th>
                  <Th hint="The role claim on that same JWT. An admin token can legitimately hold a leads:<seller_id> socket, since super_admin is allowed to watch any seller's feed.">role</Th>
                  <Th hint="How long this socket has been open. Shown as an age rather than a wall-clock timestamp because it is derived from a monotonic mark taken at connect, so a clock change on the host cannot make a long-lived socket look new.">connected for</Th>
                  <Th hint="The rec-server worker holding this socket. A client lands on exactly one worker, and only that worker's rec:events subscriber feeds it — which is why one dead subscriber silently starves just the clients listed against it.">worker</Th>
                </tr>
              </thead>
              <tbody>
                {connections.map((c, i) => (
                  <tr key={`${c.worker}-${c.channel}-${c.caller_id ?? "?"}-${i}`} className="border-t border-white/5">
                    <td className="py-1 font-mono text-slate-300">{c.channel}</td>
                    <td className={c.caller_id ? "font-mono text-slate-300" : "text-slate-500"}>
                      {c.caller_id ?? "—"}
                    </td>
                    <td className={c.role ? "text-slate-300" : "text-slate-500"}>{c.role ?? "—"}</td>
                    <td className="text-slate-300">{secs(c.connected_for_seconds)}</td>
                    <td className="max-w-[12rem] truncate font-mono text-indigo-300" title={c.worker}>
                      {c.worker}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            Sockets held by a worker that did not answer the probe are missing here, so
            this list can be shorter than the real number of connected clients.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {data.workers.map((w) => {
          const s = w.subscriber;
          return (
            <div key={w.instance_id} className="rounded-lg border border-white/10 bg-white/5 p-3">
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <p className="font-mono text-xs text-indigo-300">{w.instance_id}</p>
                <span
                  className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${
                    s.running
                      ? "bg-emerald-500/10 text-emerald-400"
                      : "bg-red-500/10 font-bold text-red-400"
                  }`}
                >
                  {s.running ? "subscriber running" : "SUBSCRIBER STOPPED"}
                </span>
                <span className="text-xs text-slate-400">
                  {num(w.total_sockets)} socket(s)
                </span>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-left text-[11px]">
                <thead className="text-slate-500">
                  <tr>
                    <Th className="py-1" hint="The Pub/Sub channel this worker's subscriber is attached to.">channel</Th>
                    <Th hint="rec:events messages this worker has relayed since it subscribed. Zero on one worker while the others climb means its fan-out is broken even though its sockets look fine.">messages recv</Th>
                    <Th hint="Idle time since this worker last received an event. A long gap is normal outside notification windows — a gap on ONE worker while the others are current is the real signal.">last message</Th>
                    <Th hint="How long this worker's subscriber has been attached. Much shorter than its siblings means this process recently restarted or re-subscribed.">subscribed for</Th>
                    <Th hint="Times this worker's subscriber dropped and re-attached. Each reconnect is a window in which events were missed outright — nothing replays them.">reconnects</Th>
                    <Th hint="The error that ended the most recent subscribe loop. Cleared once the worker successfully re-subscribes.">last error</Th>
                  </tr>
                </thead>
                <tbody>
                  {/* Every cell sets its own colour explicitly — inheriting it
                      renders dark-on-dark and the value reads as blank. */}
                  <tr className="border-t border-white/5">
                    <td className="py-1 font-mono text-slate-300">{s.channel}</td>
                    <td className="text-slate-300">{num(s.messages_received)}</td>
                    <td
                      className={
                        s.last_message_age_seconds === null ? "text-slate-500" : "text-slate-300"
                      }
                    >
                      {s.last_message_age_seconds === null
                        ? "none yet"
                        : `${secs(s.last_message_age_seconds)} ago`}
                    </td>
                    <td className="text-slate-300">{secs(s.subscribed_for_seconds)}</td>
                    <td className={s.reconnects > 0 ? "text-amber-400" : "text-slate-300"}>
                      {num(s.reconnects)}
                    </td>
                    <td className={s.last_error ? "text-amber-400" : "text-slate-500"}>
                      {s.last_error ?? "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
              </div>
              {w.channels.length > 0 && (
                <p className="mt-2 text-[11px] text-slate-500">
                  {w.channels.map((c) => `${c.channel}: ${c.sockets}`).join(" · ")}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] text-slate-500">
        "Last message" is idle time on the shared <code>rec:events</code> channel, not
        a fault on its own — outside notification windows a long gap is normal. A gap
        on ONE worker while the others are current is the real signal.
      </p>
      {data.note && <p className="mt-2 text-[11px] text-slate-500">{data.note}</p>}
    </div>
  );
}

/** Tab 2 — rec's dedicated Redis: memory, connections, namespaces, clients. */
export function RecRedisPanel() {
  const [ov, setOv] = useState<RecRedisOverviewResponse | null>(null);
  const [ns, setNs] = useState<RedisNamespacesResponse | null>(null);
  const [cl, setCl] = useState<RedisClientsResponse | null>(null);
  const [match, setMatch] = useState("*");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [o, c] = await Promise.all([
        adminService.getRecRedisOverview(),
        adminService.getRecRedisClients(),
      ]);
      setOv(o);
      setCl(c);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load rec Redis overview");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNs = useCallback(async (pattern: string) => {
    try {
      setNs(await adminService.getRecRedisNamespaces(pattern));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Namespace scan failed");
    }
  }, []);

  useEffect(() => {
    load();
    loadNs("*");
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load, loadNs]);

  if (loading) return <Spinner />;
  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!ov?.enabled) {
    return (
      <div>
        <p className="mb-4 text-sm text-slate-400">
          rec Redis unreachable: {ov?.reason ?? "unknown"}. rec_engine has no fallback
          to the shared Redis by design, so scoring runs uncached until this is back.
        </p>
        {/* Shown here too, not just below a healthy overview: this is exactly
            when the fallback counters matter most. */}
        <FallbackActivity />
      </div>
    );
  }

  const m = ov.memory, c = ov.clients, st = ov.stats;
  const cap = c?.maxclients || ov.configured_maxclients || 0;
  const connPct = cap ? ((c?.connected ?? 0) / cap) * 100 : 0;

  const scanShortcut = (pattern: string) => {
    setMatch(pattern);
    loadNs(pattern);
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <RefreshButton onRefresh={async () => { await Promise.all([load(), loadNs(match)]); }} />
      </div>
      <Warnings items={ov.warnings} />

      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Card
          label="Memory used"
          value={m?.used_human ?? num(m?.used_bytes)}
          hint="Resident memory on rec's dedicated Redis instance. On a small managed tier this, not key count, is the real ceiling — evictions begin once it is reached."
        />
        <Card
          label="Connections"
          value={`${num(c?.connected)} / ${num(cap || null)}`}
          tone={connPct > 70 ? "warn" : "ok"}
          hint="Live client connections against the server's cap. That cap is shared by the rec-server's own pool AND every backend worker's publish-only pool, so neither side's pool size explains this number alone."
        />
        <Card
          label="Rejected connections"
          value={num(st?.rejected_connections)}
          tone={(st?.rejected_connections ?? 0) > 0 ? "bad" : "ok"}
          hint="Connections Redis refused outright because maxclients was reached. Any non-zero value means some cache read or rec:events publish failed — cross-check the fallback counters below to see which."
        />
        <Card
          label="Ops/sec"
          value={num(st?.ops_per_sec)}
          hint="Commands per second the instance is serving right now. Useful as a baseline: a sudden drop while sockets stay connected suggests callers have stopped reaching it rather than traffic falling."
        />
        <Card
          label="Hit rate"
          value={st?.hit_rate_pct != null ? `${st.hit_rate_pct}%` : "—"}
          tone={(st?.hit_rate_pct ?? 100) < 50 ? "warn" : "ok"}
          hint="Share of key lookups that found a value, since the instance started. rec_engine's keys expire in 5 and 15 minutes, so a middling rate is normal; a collapse points at eviction or a changed key format."
        />
        <Card
          label="Evicted keys"
          value={num(st?.evicted_keys)}
          tone={(st?.evicted_keys ?? 0) > 0 ? "warn" : "ok"}
          hint="Keys Redis deleted EARLY to reclaim memory, before their TTL. Non-zero means the instance is memory-bound and is taking cache hits away — unlike expiry, this is not normal operation."
        />
        <Card
          label="Expired keys"
          value={num(st?.expired_keys)}
          hint="Keys removed at the natural end of their TTL. This is what the 5min/15min caches are supposed to do, so a large number here is healthy, not a fault."
        />
        <Card
          label="Eviction policy"
          value={m?.maxmemory_policy ?? "—"}
          hint="What Redis discards when memory fills. An LRU policy is safe here because everything on this instance is a rebuildable cache; noeviction would instead turn a full instance into hard write errors."
        />
      </div>

      <div className="mb-4 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-slate-500">
        This is rec_engine's <span className="text-slate-200">own dedicated Redis Cloud
        instance</span> (REC_REDIS_URL) — nothing here is shared with the backend, RAG
        or OCR Redis shown on the WebSocket Health tab. Its configured connection cap
        is <span className="text-slate-200">{num(ov.configured_maxclients ?? null)}</span>{" "}
        (REC_REDIS_MAXCLIENTS)
        {c?.maxclients != null && ov.configured_maxclients != null
          && c.maxclients !== ov.configured_maxclients
          ? `, but the server currently reports ${num(c.maxclients)} — a managed plan
             can enforce its own limit regardless of CONFIG SET.`
          : "."}{" "}
        On a 250MB tier, memory rather than key count is the ceiling to watch.
      </div>

      <div className="mb-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-semibold text-slate-200">Key namespaces</h4>
          <input
            value={match}
            onChange={(e) => setMatch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") loadNs(match); }}
            placeholder="SCAN pattern, e.g. recs:*"
            className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-200"
          />
          <button
            type="button"
            onClick={() => loadNs(match)}
            className="rounded-md border border-white/10 px-2 py-1 text-xs text-indigo-300 hover:bg-white/5"
          >
            Scan
          </button>
          {(ov.key_patterns ?? []).map((p) => (
            <button
              key={p.pattern}
              type="button"
              onClick={() => scanShortcut(p.pattern)}
              title={`${p.label} — ${p.ttl_seconds}s TTL`}
              className="rounded-md border border-white/10 px-2 py-1 font-mono text-xs text-slate-400 hover:bg-white/5"
            >
              {p.pattern}
            </button>
          ))}
          {ns?.truncated && (
            <span className="text-[11px] text-amber-400">
              truncated at {num(ns.scanned)} keys
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-slate-500">
            <tr>
              <Th className="py-1" hint="Key prefix up to the first colon. rec_engine writes only bsig: and recs: — anything else on this instance came from somewhere it should not have.">namespace</Th>
              <Th hint="Keys counted under that prefix by the SCAN above. The scan is capped, so watch for the 'truncated' marker before reading this as a total.">keys</Th>
              <Th hint="Keys with no expiry set. rec_engine puts a TTL on everything it writes, so any count here will grow forever until something explains it.">without TTL</Th>
            </tr>
          </thead>
          <tbody>
            {(ns?.namespaces ?? []).slice(0, 25).map((r) => (
              <tr key={r.namespace} className="border-t border-white/5">
                <td className="py-1 font-mono text-slate-300">{r.namespace}</td>
                <td className="text-slate-300">{num(r.keys)}</td>
                <td className={r.keys_without_ttl > 0 ? "font-semibold text-amber-400" : "text-slate-500"}>
                  {num(r.keys_without_ttl)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          rec_engine writes only two key families, both with a TTL:{" "}
          {(ov.key_patterns ?? []).map((p) => (
            <span key={p.pattern}>
              <code>{p.pattern}</code> ({p.label}, {p.ttl_seconds}s){" "}
            </span>
          ))}
          — anything else, and any key here <em>without</em> a TTL, is unexpected on
          this instance and worth explaining before it grows.
        </p>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-semibold text-slate-200">
          Live connections — {num(cl?.total)} total, {num(cl?.subscribers)} long-lived subscribers
        </h4>
        <p className="mb-2 text-[11px] text-slate-500">
          Two different processes land here: the rec-server's own pool (one per rec
          worker) and every backend API worker's smaller publish_event()-only pool.
          Each subscriber permanently holds one slot of its pool.
        </p>
        <div className="max-h-72 overflow-x-auto overflow-y-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="sticky top-0 bg-slate-900 text-slate-500">
              <tr>
                <Th className="py-1" hint="Client IP and port of the connection as Redis sees it. Several rows share a host when that host runs multiple workers.">addr</Th>
                <Th hint="The connection's CLIENT SETNAME value. rec_engine's pools name themselves rec-engine-<hostname>, so an unnamed connection is something else — another service or a redis-cli session.">name</Th>
                <Th hint="Seconds since this connection last ran a command. A long idle on a non-subscriber is a pool slot being held for nothing.">idle</Th>
                <Th hint="The last command this connection issued. 'subscribe' marks a long-lived subscriber that permanently occupies one slot of its pool.">cmd</Th>
                <Th hint="Plain-English reading of that last command, so an unexpected connection can be identified without knowing the Redis protocol.">what it's doing</Th>
              </tr>
            </thead>
            <tbody>
              {(cl?.clients ?? []).slice(0, 80).map((r, i) => (
                <tr key={`${r.addr}-${i}`} className="border-t border-white/5">
                  <td className="py-1 font-mono text-slate-400">{r.addr}</td>
                  <td className="font-mono text-slate-500">{r.name || "—"}</td>
                  <td className="text-slate-500">{r.idle_seconds}s</td>
                  <td className={r.is_subscriber ? "font-semibold text-indigo-300" : "text-slate-300"}>{r.cmd}</td>
                  <td className="text-slate-500">{r.cmd_explained}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FallbackActivity />
    </div>
  );
}

/**
 * Is anything actually falling back off Redis right now?
 *
 * The cards above describe the INSTANCE; these describe the CALLERS. Every
 * Redis touch in rec_engine is wrapped in try/except, so an outage is served
 * correctly by a slower path and leaves no trace beyond latency — the instance
 * can look reachable from here while every scorer request is recomputing from
 * Mongo. These counters are incremented only inside those except branches, so
 * an expired key is never counted and any non-zero number means Redis itself
 * failed.
 */
function FallbackActivity() {
  const [data, setData] = useState<RecFallbackHealthResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await adminService.getRecFallbackHealth());
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load fallback counters");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="mt-6 border-t border-white/10 pt-5">
      <div className="mb-1 flex items-center justify-between">
        <h4 className="flex items-center text-sm font-semibold text-slate-200">
          Fallback activity — is Redis being bypassed right now?
          <InfoTooltip text="Counts of the moments rec_engine actually degraded off Redis, taken from inside the except branch of each Redis call. The panels above show whether the instance is up; this shows whether callers are still using it." />
        </h4>
        <RefreshButton onRefresh={load} />
      </div>
      <p className="mb-3 text-[11px] text-slate-500">
        Counted only when a Redis call <em>raised</em>. A key that simply expired
        (bsig 5min, recs 15min) happens constantly against a healthy instance and is
        never counted here, so every number below is a real failure.
      </p>

      {err && <p className="mb-3 text-xs text-red-400">{err}</p>}
      {!data && !err && <Spinner />}

      {data && (
        <>
          <div className="grid gap-3 md:grid-cols-2">
            <FallbackProcessCard
              title="rec-server worker"
              subtitle={`one of ${num(data.expected_rec_workers ?? null)} workers — the process that runs scoring`}
              titleHint="The rec-server worker that served this request. The scorer's bsig/recs cache reads only ever run here, so cache-read failures can only be seen from this column. It is one worker's share, not the cluster total."
              snap={data.rec_server}
            />
            <FallbackProcessCard
              title="backend API worker"
              subtitle="this API worker — reaches rec's Redis only to publish rec:events"
              titleHint="The backend worker answering this page. It never reads rec's caches, so only the dropped-events counter can move here — but publish_event() does fire from backend write paths (analytics consumer, leads service), which a rec-server-only view would hide."
              snap={data.backend_api}
            />
          </div>
          {data.note && <p className="mt-3 text-[11px] text-slate-500">{data.note}</p>}
        </>
      )}
    </div>
  );
}

function FallbackProcessCard({
  title,
  subtitle,
  titleHint,
  snap,
}: {
  title: string;
  subtitle: string;
  titleHint: string;
  snap: RecFallbackProcessSnapshot;
}) {
  if (snap?.reachable === false) {
    return (
      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
        <p className="flex items-center text-xs font-semibold text-slate-200">
          {title}
          <InfoTooltip text={titleHint} />
        </p>
        <p className="mt-2 text-xs text-amber-400">
          {snap.error ?? "unreachable"} — these counters live in that process's memory,
          so nothing else can report them.
        </p>
      </div>
    );
  }
  if (!snap) return null;

  const anyFallback = snap.total_fallback_events > 0;

  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="flex items-center text-xs font-semibold text-slate-200">
          {title}
          <InfoTooltip text={titleHint} />
        </p>
        <span
          className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${
            snap.redis_currently_healthy
              ? "bg-emerald-500/10 text-emerald-400"
              : "bg-red-500/10 font-bold text-red-400"
          }`}
        >
          {snap.redis_currently_healthy ? "redis reachable" : "REDIS UNREACHABLE"}
          <InfoTooltip
            text={
              snap.redis_currently_healthy
                ? "A live PING issued when this panel loaded, from inside that process. The client object is cached after its first successful connect and is handed back unchanged mid-outage, so only a PING says anything about right now."
                : `That process cannot reach rec's Redis at this moment, so everything it does is running on the fallback path. ${snap.redis_probe_error ?? ""}`
            }
          />
        </span>
        <span className="inline-flex items-center font-mono text-[10px] text-slate-500">
          {snap.instance_id} · up {secs(snap.uptime_seconds)}
          <InfoTooltip text="Process identity and how long it has been up. Counters are in-memory and reset on restart, so they only mean something relative to this uptime." />
        </span>
      </div>

      <p className="mb-2 text-[11px] text-slate-500">{subtitle}</p>

      <div className="grid grid-cols-2 gap-2">
        <Card
          label="Total fallback events"
          value={num(snap.total_fallback_events)}
          tone={anyFallback ? "warn" : "ok"}
          hint="Every Redis error on this process since it started — cache reads, cache writes and dropped events combined. Read it against the uptime beside the title: a handful over days is a blip, a number climbing while you watch is an outage in progress."
        />
        <Card
          label="Buyer-signal cache errors"
          value={num(snap.bsig_cache_errors)}
          tone={snap.bsig_cache_errors > 0 ? "warn" : "ok"}
          hint="Times the buyer-signal cache read failed and the request fell back to a direct Mongo lookup. High or rising means Redis is degraded, not necessarily down. A key that merely expired is not counted."
        />
        <Card
          label="Mongo fallback reads"
          value={num(snap.mongo_fallback_reads)}
          tone={snap.mongo_fallback_reads > 0 ? "warn" : "ok"}
          hint="The subset of buyer-signal cache errors that actually issued a buyer_signals query the cache would have saved. This is the extra Mongo load Redis failures have cost — the query is indexed on user_id, so it is slower, not dangerous."
        />
        <Card
          label="Recs cache errors"
          value={num(snap.recs_cache_errors)}
          tone={snap.recs_cache_errors > 0 ? "warn" : "ok"}
          hint="Times the ranked-recommendation cache read failed, forcing a full recompute (candidate retrieval plus scoring) instead of slicing the cached list. Much more expensive per event than a buyer-signal fallback."
        />
        <Card
          label="Cache write errors"
          value={num(snap.cache_write_errors)}
          tone={snap.cache_write_errors > 0 ? "warn" : "ok"}
          hint="Times a cache write (bsig or recs SETEX) failed and was skipped. The request that hit this was still served correctly; the cost is that later requests have nothing to hit, so these turn into read misses shortly afterwards."
        />
        <Card
          label="Dropped rec:events"
          value={num(snap.publish_event_drops)}
          tone={snap.publish_event_drops > 0 ? "bad" : "ok"}
          hint="rec:events messages lost because the publish raised. This path is fire-and-forget by design, so there is no Mongo fallback and nothing retries — every drop is an update that connected admin and seller panels never received."
        />
      </div>

      <div className="mt-2 space-y-0.5 text-[11px] text-slate-500">
        <p className="inline-flex items-center">
          last bsig error: {localTime(snap.last_bsig_cache_error_at)}
          <InfoTooltip text="When this process last failed a buyer-signal cache read." />
        </p>
        <p className="inline-flex items-center">
          last recs error: {localTime(snap.last_recs_cache_error_at)}
          <InfoTooltip text="When this process last failed a ranked-recommendation cache read." />
        </p>
        <p className="inline-flex items-center">
          last write error: {localTime(snap.last_cache_write_error_at)}
          <InfoTooltip text="When this process last failed to write a cache entry." />
        </p>
        <p className="inline-flex items-center">
          last dropped event: {localTime(snap.last_publish_drop_at)}
          <InfoTooltip text="When this process last dropped a rec:events message." />
        </p>
        {snap.last_error?.message && (
          <p className="inline-flex items-center break-words text-amber-400">
            last error ({snap.last_error.site}): {snap.last_error.message}
            <InfoTooltip text="The most recent Redis exception on this process, and which call site raised it. One shared slot rather than one per counter: during an outage every site fails with the same error, and what matters is what Redis is actually saying." />
          </p>
        )}
      </div>
    </div>
  );
}

/** Tab 3 — are the scheduled buyer/seller intel jobs actually running? */
export function RecJobsPanel() {
  const [data, setData] = useState<RecJobHealthResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await adminService.getRecJobHealth());
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load job health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <Spinner />;
  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!data) return null;

  const warnings: string[] = [];
  if (data.overdue_count > 0) {
    warnings.push(
      `${data.overdue_count} job(s) have not completed within a healthy multiple of ` +
        `their own schedule. A scheduled job that stops firing produces no error — ` +
        `signals, retrains and notification triggers just quietly stop happening.`,
    );
  }
  if (data.failing_count > 0) {
    warnings.push(
      `${data.failing_count} job(s) ended their last run with an error. See the ` +
        `"last error" column; the lock is still released on failure, so the next ` +
        `tick will retry.`,
    );
  }

  const engines: Array<"buyer" | "seller"> = ["buyer", "seller"];

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <RefreshButton onRefresh={load} />
      </div>
      <Warnings items={warnings} />

      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Card
          label="Running now"
          value={num(data.running_count)}
          hint="Jobs whose worker lock is held at this moment. locked_job() gates each fired tick to exactly one of the four rec-server workers, so this means one worker is executing — not that four copies are racing."
        />
        <Card
          label="Overdue"
          value={num(data.overdue_count)}
          tone={data.overdue_count ? "bad" : "ok"}
          hint="Jobs that have not completed within 2.5x their own schedule. A scheduled job that stops firing raises nothing anywhere — signal aggregation, retrains and notification triggers just quietly stop happening."
        />
        <Card
          label="Last run failed"
          value={num(data.failing_count)}
          tone={data.failing_count ? "bad" : "ok"}
          hint="Jobs whose most recent run ended in an error. The lock is still released on failure, so the next scheduled tick will retry rather than wedging."
        />
        <Card
          label="No record yet"
          value={num(data.never_recorded_count)}
          tone={data.never_recorded_count ? "warn" : "ok"}
          hint="Jobs with no run recorded at all. Expected for up to a week on weekly jobs, because this bookkeeping only accumulates from the first tick after it was deployed — ambiguous rather than broken."
        />
      </div>

      {engines.map((engine) => {
        const rows = data.jobs.filter((j) => j.engine === engine);
        if (rows.length === 0) return null;
        return (
          <div key={engine} className="mb-5">
            <h4 className="mb-2 text-sm font-semibold capitalize text-slate-200">
              {engine} intel — {rows.length} scheduled jobs
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11px]">
                <thead className="text-slate-500">
                  <tr>
                    <Th className="py-1" hint="The job's label, with the locked_job() name it is registered under underneath — that name is what the worker_locks records key on.">job</Th>
                    <Th hint="The cadence this job's add_job() call declares. 'Overdue' below is measured against it, so a job with no interval (once at startup) can never be overdue.">schedule</Th>
                    <Th hint="running = a worker holds the lock right now; OVERDUE = it has missed its window with slack; idle = waiting for the next tick.">state</Th>
                    <Th hint="How long ago this job last finished, with the absolute local time beneath. 'no record' means it has not completed since this bookkeeping was deployed.">last run</Th>
                    <Th hint="Whether that last completed run ended ok or with an error.">outcome</Th>
                    <Th hint="Wall-clock duration of the last run. A retrain creeping upward is worth catching before it starts overrunning its own interval.">took</Th>
                    <Th hint="Completed runs recorded for this job since run bookkeeping was deployed — not since the job first existed.">runs</Th>
                    <Th hint="How many of those recorded runs ended in an error. A job that alternates between ok and error is failing on specific data, not on schedule.">errors</Th>
                    <Th hint="The message from the most recent failure. It stays visible after a later successful run, so check the outcome column before acting on it.">last error</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((j) => (
                    <tr key={j.name} className="border-t border-white/5">
                      <td className="py-1 text-slate-300">
                        <div>{j.label}</div>
                        <div className="font-mono text-[10px] text-slate-600">{j.name}</div>
                      </td>
                      <td className="text-slate-400">{j.schedule}</td>
                      <td
                        className={
                          j.locked
                            ? "font-semibold text-indigo-300"
                            : j.overdue
                              ? "font-bold text-red-400"
                              : "text-slate-400"
                        }
                      >
                        {j.locked
                          ? `running (${j.lock_backend})`
                          : j.overdue
                            ? "OVERDUE"
                            : "idle"}
                        {j.locked && j.lock_ttl_seconds != null && (
                          <div className="text-[10px] font-normal text-slate-500">
                            lock expires in {secs(j.lock_ttl_seconds)}
                          </div>
                        )}
                      </td>
                      <td className={j.never_recorded ? "text-slate-500" : "text-slate-300"}>
                        {j.never_recorded ? "no record" : `${secs(j.last_run_age_seconds)} ago`}
                        {!j.never_recorded && (
                          <div className="text-[10px] text-slate-600">
                            {localTime(j.last_finished_at)}
                          </div>
                        )}
                      </td>
                      <td
                        className={
                          j.last_status === "error"
                            ? "font-semibold text-red-400"
                            : j.last_status === "ok"
                              ? "text-emerald-400"
                              : "text-slate-500"
                        }
                      >
                        {j.last_status ?? "—"}
                      </td>
                      <td className="text-slate-400">
                        {j.last_duration_ms != null ? `${num(j.last_duration_ms)}ms` : "—"}
                      </td>
                      <td className="text-slate-400">{num(j.run_count ?? 0)}</td>
                      <td className={(j.error_count ?? 0) > 0 ? "text-amber-400" : "text-slate-400"}>
                        {num(j.error_count ?? 0)}
                      </td>
                      <td className="max-w-xs break-words text-slate-500">
                        {j.last_error ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <p className="text-[11px] text-slate-500">
        Every job above is wrapped in <code>locked_job()</code>, so exactly one of the
        four rec-server workers executes each fired tick — "running" therefore means
        one worker holds the lock, not that four copies are racing. That gating is
        also why this table is needed: before run bookkeeping existed, "one worker ran
        it" and "no worker ran it" looked identical from the outside.
      </p>
      {data.note && <p className="mt-2 text-[11px] text-slate-500">{data.note}</p>}
    </div>
  );
}
